'use client';

/**
 * A movable, resizable panel — the shell every popup in the app sits in.
 *
 * Modals here are not "confirm and dismiss" dialogs; they are work surfaces.
 * You search a taxonomy in one while reading service names off the diagram
 * behind it, you diff two Terraform versions in another. A centred box of a
 * fixed size covers exactly the thing you opened it to look at, and there was
 * nothing you could do about it.
 *
 * So: drag the header to move, drag any edge or corner to resize, and both are
 * remembered per window id, because someone who moves the Add-a-service panel
 * off their diagram wants it there next time too. Double-click the header to
 * put it back where it started.
 *
 * Geometry is `position: fixed` in viewport pixels rather than a flex-centred
 * box, since dragging has to write coordinates somewhere. Everything is
 * clamped so a window can never be dropped past the edge of the screen with
 * its header — and therefore its close button — out of reach.
 *
 * **It renders into `<body>` through a portal, never where it was declared.**
 * That is not tidiness, it is the only thing that makes `position: fixed`
 * mean what it says. A non-`none` `backdrop-filter`, `filter`, `transform` or
 * `perspective` on *any* ancestor makes that ancestor the containing block for
 * fixed-position descendants — so a window opened from inside one of the
 * frosted panels (`.float-panel`, `.float-card`, both of which blur their
 * backdrop) would resolve its coordinates against that 21rem popover and get
 * clipped by its `overflow-hidden`, instead of covering the viewport.
 *
 * That failure is also environment-dependent, which is the worst part: Chrome
 * only started creating the containing block for `backdrop-filter` at a
 * particular version, and Lite mode (`html.lite-gfx`) switches the property
 * off entirely — so the same build renders correctly on one machine and
 * broken on the next. The portal removes the whole class of problem rather
 * than the one instance of it, and callers can go on declaring a window
 * wherever it makes sense in the tree.
 *
 * `<body>` is the destination only while nothing is fullscreen; see
 * lib/overlayRoot for why it has to follow the fullscreen element when the
 * canvas is showing a diagram full screen.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@/components/ui/AppIcon';
import { useOverlayRoot } from '@/lib/overlayRoot';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

type Dir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface FloatingWindowProps {
  /** Stable key for the remembered position and size. */
  id: string;
  title: string;
  subtitle?: string;
  /** Heroicon name, shown in a tile at the head of the title bar. */
  icon?: string;
  onClose: () => void;
  /** Opening size in px, before the user has resized it. */
  width?: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
  /** Where it opens. 'right'/'left' dock it to that edge, inset from it. */
  placement?: 'center' | 'right' | 'left';
  /** How much to dim the page behind. Flyouts stay light on purpose. */
  scrim?: 'none' | 'light' | 'heavy';
  /** Dismiss when the scrim is clicked. */
  closeOnScrim?: boolean;
  /**
   * Dismiss on Escape. Only ever true for the topmost window, so this is for
   * the case where the *window itself* has something stacked inside it that
   * should take the key first -- a slide-over panel, a run in progress.
   */
  closeOnEscape?: boolean;
  /**
   * Height follows the content and the resize grips are hidden. For short
   * confirmations, where a resize handle on a three-line box is just noise.
   */
  autoHeight?: boolean;
  /** Rendered in the title bar, before the close button. */
  headerExtra?: React.ReactNode;
  /** Pinned under the scrolling body. */
  footer?: React.ReactNode;
  zIndex?: number;
  /**
   * False when the body manages its own scrolling -- a two-pane editor, say.
   * A prop rather than an `overflow-hidden` in bodyClassName, because both
   * are Tailwind utilities and which one wins is decided by their order in
   * the generated stylesheet, not by the order in the class attribute.
   */
  scroll?: boolean;
  /** Extra classes on the body. Padding is the caller's business. */
  bodyClassName?: string;
  /**
   * The gradient rule across the very top of the window. Every popup gets one
   * by default so the whole family reads as one surface; pass a CSS background
   * to tint a particular window, or `null` to drop the rule entirely.
   */
  accent?: string | null;
  children: React.ReactNode;
}

/** The house gradient -- the same rule the Generator panel wears. */
export const WINDOW_ACCENT = 'linear-gradient(90deg, #7c3aed 0%, #2563eb 55%, #06b6d4 100%)';

/** Keeps at least this much of the title bar on screen, whatever you do. */
const KEEP_VISIBLE = 140;
const HEADER_H = 44;
const MARGIN = 8;

const CURSORS: Record<Dir, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

function storageKey(id: string) {
  return `archlyy.win.${id}`;
}

/* Mount order of every open window, so Escape can be handled by the top one
   only. Each window listens on `window`, so without this a confirmation opened
   over the Terraform editor would take Escape *and* hand the same keypress to
   the editor underneath -- cancelling the dialog would close the editor too. */
const STACK: symbol[] = [];

/** Where a window sits when it has never been moved. */
function defaultRect(width: number, height: number, placement: 'center' | 'right' | 'left'): Rect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(width, vw - MARGIN * 2);
  const h = Math.min(height, vh - MARGIN * 2 - 48);
  if (placement === 'right') return { x: vw - w - 12, y: 56, w, h };
  if (placement === 'left') return { x: 12, y: 56, w, h };
  return { x: Math.round((vw - w) / 2), y: Math.max(56, Math.round((vh - h) / 2)), w, h };
}

function clampRect(r: Rect, minWidth: number, minHeight: number, autoHeight: boolean): Rect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.max(minWidth, Math.min(r.w, vw - MARGIN * 2));
  const h = autoHeight ? r.h : Math.max(minHeight, Math.min(r.h, vh - MARGIN * 2));
  /* Horizontally a window may hang off either edge, as long as a grabbable
     slice of the title bar stays on screen. Vertically the top edge can never
     go above the viewport, because the title bar is the only way to drag it
     back down. */
  const x = Math.min(Math.max(r.x, KEEP_VISIBLE - w), vw - KEEP_VISIBLE);
  const y = Math.min(Math.max(r.y, MARGIN), vh - HEADER_H);
  return { x, y, w, h };
}

function readStored(id: string): Rect | null {
  try {
    const raw = localStorage.getItem(storageKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Rect>;
    if (
      typeof parsed?.x !== 'number' ||
      typeof parsed?.y !== 'number' ||
      typeof parsed?.w !== 'number' ||
      typeof parsed?.h !== 'number'
    ) {
      return null;
    }
    return parsed as Rect;
  } catch {
    return null;
  }
}

export default function FloatingWindow({
  id,
  title,
  subtitle,
  icon,
  onClose,
  width = 520,
  height = 560,
  minWidth = 320,
  minHeight = 200,
  placement = 'center',
  scrim = 'heavy',
  closeOnScrim = true,
  closeOnEscape = true,
  autoHeight = false,
  headerExtra,
  footer,
  zIndex = 60,
  scroll = true,
  bodyClassName = '',
  accent = WINDOW_ACCENT,
  children,
}: FloatingWindowProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const overlayRoot = useOverlayRoot();

  /* Resolved during the *first* render, not in an effect.
     An effect would mean returning null on the first pass, so the children
     would not be in the DOM yet -- and half of these windows autofocus a
     field from their own mount effect (the service search, the prompt box).
     Those effects run before a parent's layout effect, so the focus call
     would land on a ref that is still null and silently do nothing. */
  const [rect, setRect] = useState<Rect>(() => {
    if (typeof window === 'undefined') return { x: 0, y: 56, w: width, h: height };
    return clampRect(
      readStored(id) ?? defaultRect(width, height, placement),
      minWidth,
      minHeight,
      autoHeight
    );
  });
  const [gesture, setGesture] = useState<'move' | Dir | null>(null);
  /* Mirrors `rect` so the pointerup handler can save the final geometry
     without reading it out of a state updater -- updaters must stay pure, and
     under StrictMode they run twice. */
  const latest = useRef<Rect>(rect);
  latest.current = rect;

  const clamp = useCallback(
    (r: Rect): Rect => clampRect(r, minWidth, minHeight, autoHeight),
    [autoHeight, minWidth, minHeight]
  );

  /* Identity for the Escape stack. A ref so it survives re-renders; the
     effect below owns pushing and popping it. */
  const token = useRef<symbol>(Symbol(id));

  useEffect(() => {
    const mine = token.current;
    STACK.push(mine);
    return () => {
      const at = STACK.lastIndexOf(mine);
      if (at >= 0) STACK.splice(at, 1);
    };
  }, []);

  useEffect(() => {
    if (!closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      // Only the window on top of the stack answers Escape.
      if (e.key !== 'Escape' || STACK[STACK.length - 1] !== token.current) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, closeOnEscape]);

  // A window parked against the right edge must not fall off it when the
  // browser window shrinks.
  useEffect(() => {
    const onResize = () => setRect((r) => clamp(r));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  const persist = useCallback(
    (r: Rect) => {
      try {
        localStorage.setItem(storageKey(id), JSON.stringify(r));
      } catch {
        // Private mode or a full quota: the window still works, it just
        // reopens where it started.
      }
    },
    [id]
  );

  /** One handler for both gestures -- they differ only in how the rect moves. */
  const beginGesture = useCallback(
    (e: React.PointerEvent, kind: 'move' | Dir) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const start = { ...rect };
      const originX = e.clientX;
      const originY = e.clientY;
      const measuredH = panelRef.current?.offsetHeight ?? start.h;

      setGesture(kind);
      // Otherwise a drag selects the title text and every label it passes over.
      const priorSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - originX;
        const dy = ev.clientY - originY;

        if (kind === 'move') {
          setRect(clamp({ ...start, h: measuredH, x: start.x + dx, y: start.y + dy }));
          return;
        }

        let { x, y, w, h } = { ...start, h: measuredH };
        /* Dragging a top or left edge moves the origin as well as the size, so
           the opposite edge stays put -- the whole point of grabbing that
           side. The min-size clamps are applied to the delta first, so the
           window stops growing in the other direction once it bottoms out. */
        if (kind.includes('e')) w = start.w + dx;
        if (kind.includes('s')) h = measuredH + dy;
        if (kind.includes('w')) {
          const next = Math.max(minWidth, start.w - dx);
          x = start.x + (start.w - next);
          w = next;
        }
        if (kind.includes('n')) {
          const next = Math.max(minHeight, measuredH - dy);
          y = start.y + (measuredH - next);
          h = next;
        }
        setRect(clamp({ x, y, w, h }));
      };

      const onUp = () => {
        document.body.style.userSelect = priorSelect;
        setGesture(null);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        if (latest.current) persist(latest.current);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [rect, clamp, minWidth, minHeight, persist]
  );

  /** Escape hatch for a window dragged somewhere unhelpful. */
  const resetGeometry = useCallback(() => {
    try {
      localStorage.removeItem(storageKey(id));
    } catch {
      /* nothing to clear */
    }
    setRect(clamp(defaultRect(width, height, placement)));
  }, [id, clamp, width, height, placement]);

  const grip = (dir: Dir, className: string) => (
    <div
      key={dir}
      onPointerDown={(e) => beginGesture(e, dir)}
      className={`absolute ${className}`}
      style={{ cursor: CURSORS[dir], touchAction: 'none' }}
      aria-hidden
    />
  );

  const window_ = (
    <div
      className="fixed inset-0 animate-fade-in"
      style={{
        zIndex,
        // A window you can drag off the scrim is still modal; the scrim just
        // stops interaction with what is behind it.
        background:
          scrim === 'none'
            ? 'transparent'
            : scrim === 'light'
              ? 'rgba(0,0,0,0.32)'
              : 'var(--overlay-bg)',
        pointerEvents: scrim === 'none' ? 'none' : 'auto',
      }}
      onPointerDown={() => closeOnScrim && onClose()}
    >
      <div
        ref={panelRef}
        className="fixed flex flex-col rounded-xl overflow-hidden"
        style={{
          left: rect.x,
          top: rect.y,
          width: rect.w,
          height: autoHeight ? undefined : rect.h,
          maxHeight: autoHeight ? `calc(100vh - ${rect.y + MARGIN}px)` : undefined,
          background: 'var(--panel-bg)',
          boxShadow: 'var(--popover-shadow)',
          pointerEvents: 'auto',
          // No transition on the geometry: dragging must track the pointer
          // exactly, and an eased `left` lags behind it visibly.
          transition: 'none',
        }}
        onPointerDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* A gradient rule across the top of every window, matching the one on
            the Generator panel. On the light theme a popup was a white card on
            an almost-white page whose only edge was a soft shadow; this is
            what tells you where it starts. It sits above the title bar rather
            than inside it so it survives whatever a caller puts in the
            header, and the panel's own overflow-hidden clips it to the
            rounded corners. */}
        {accent && (
          <div className="h-[3px] flex-shrink-0" style={{ background: accent }} aria-hidden />
        )}

        {/* Title bar = drag handle. */}
        <div
          className="flex items-start gap-3 px-4 py-2.5 border-b border-border flex-shrink-0 select-none"
          style={{ cursor: gesture === 'move' ? 'grabbing' : 'grab', touchAction: 'none' }}
          onPointerDown={(e) => beginGesture(e, 'move')}
          onDoubleClick={resetGeometry}
          title="Drag to move · double-click to reset the position and size"
        >
          {icon && (
            <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
              <Icon name={icon} size={17} className="text-primary" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold truncate">{title}</h2>
            {subtitle && <p className="text-2xs text-muted-foreground truncate">{subtitle}</p>}
          </div>
          {/* Controls sit inside the handle, so their own pointerdown must not
              start a drag. */}
          <div
            className="flex items-center gap-1 flex-shrink-0"
            onPointerDown={(e) => e.stopPropagation()}
            style={{ cursor: 'default' }}
          >
            {headerExtra}
            <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>
        </div>

        <div
          className={`flex-1 min-h-0 ${scroll ? 'overflow-y-auto' : 'overflow-hidden'} ${bodyClassName}`}
        >
          {children}
        </div>

        {footer && <div className="flex-shrink-0 border-t border-border">{footer}</div>}

        {!autoHeight && (
          <>
            {/* Edges, then corners -- corners are declared later so they win
                the overlap and you get the diagonal cursor there. */}
            {grip('n', 'top-0 left-3 right-3 h-1.5')}
            {grip('s', 'bottom-0 left-3 right-3 h-1.5')}
            {grip('w', 'left-0 top-3 bottom-3 w-1.5')}
            {grip('e', 'right-0 top-3 bottom-3 w-1.5')}
            {grip('nw', 'top-0 left-0 w-3.5 h-3.5')}
            {grip('ne', 'top-0 right-0 w-3.5 h-3.5')}
            {grip('sw', 'bottom-0 left-0 w-3.5 h-3.5')}
            {grip('se', 'bottom-0 right-0 w-4 h-4')}

            {/* The only grip drawn rather than invisible: without one visible
                affordance nobody discovers that these windows resize at all. */}
            <div
              className="absolute bottom-0.5 right-0.5 pointer-events-none opacity-40"
              aria-hidden
            >
              <svg width="12" height="12" viewBox="0 0 12 12">
                <path
                  d="M11 4 L4 11 M11 8 L8 11"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </div>
          </>
        )}
      </div>
    </div>
  );

  /* No document during the static export's prerender pass. Safe to return
     null there rather than deferring to an effect: a window is only ever
     mounted by a user action, so there is nothing to prerender and no
     hydration mismatch to create. */
  if (!overlayRoot) return null;
  return createPortal(window_, overlayRoot);
}
